// components/views/profile/QboSetupWizard.jsx
import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import QboAPI from '../../../services/qboService';
import './QboSetupWizard.css';

// Account mapping configuration
const ACCOUNT_ROWS = [
  { key: 'stripe_clearing', label: 'Stripe Clearing', group: 'gl', mapType: 'account_map', filterType: 'Other Current Asset', required: true, desc: 'Where Stripe payments land before payout' },
  { key: 'accounts_receivable', label: 'Accounts Receivable', group: 'gl', mapType: 'account_map', filterType: 'Accounts Receivable', required: true, desc: 'Used as ARAccountRef on invoices' },
  { key: 'jobvious_platform_fees', label: 'Jobvious Platform Fees', group: 'gl', mapType: 'account_map', filterType: 'Expense', required: true, desc: 'Platform fee expenses on payouts' },
  { key: 'chargebacks', label: 'Chargebacks', group: 'gl', mapType: 'account_map', filterType: 'Expense', required: true, desc: 'Chargeback/dispute expenses' },
  { key: 'software_subscriptions', label: 'Software Subscriptions', group: 'gl', mapType: 'account_map', filterType: 'Expense', required: true, desc: 'Jobvious subscription charges' },
  { key: 'tips_payable', label: 'Tips Payable', group: 'gl', mapType: 'account_map', filterType: 'Other Current Liability', required: true, desc: 'Card tips held for technician payroll' },
  { key: 'default', label: 'Default Bank Account', group: 'bank', mapType: 'bank_account_map', filterType: 'Bank', required: true, desc: 'Where Stripe payouts deposit to' }
];

const GROUP_LABELS = {
  gl: 'GL Accounts',
  bank: 'Bank Account'
};

// Account type keywords used to filter the QBO chart of accounts dropdown
// Server may return QBO-native PascalCase (AccountType) or normalized lowercase (type/subType)
const getType = (a) => a.AccountType || a.accountType || a.type || '';
const getSubType = (a) => a.AccountSubType || a.accountSubType || a.subType || '';

const TYPE_FILTERS = {
  'Other Current Asset': (a) => getType(a) === 'Other Current Asset',
  'Accounts Receivable': (a) => getSubType(a) === 'AccountsReceivable' || getType(a) === 'Accounts Receivable',
  'Expense': (a) => getType(a) === 'Expense',
  'Other Current Liability': (a) => getType(a) === 'Other Current Liability',
  'Bank': (a) => getType(a) === 'Bank'
};

class QboSetupWizard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: 1,
      loading: true,
      loadError: null,
      detected: {},
      missing: [],
      allAccounts: [],
      mappings: {},       // { accountKey: { accountId, createInQbo } }
      saving: false,
      activating: false,
      saveError: null
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.loadSetupAccounts();
    }
  }

  loadSetupAccounts = async () => {
    this.setState({ loading: true, loadError: null, currentStep: 1, saveError: null });
    try {
      const result = await QboAPI.getSetupAccounts(this.props.installerId);
      console.log('[QboSetupWizard] raw setup response:', JSON.stringify(result, null, 2));

      const detected = result.detected || result.mappings || {};
      const missing = result.missing || [];
      const allAccounts = result.accounts || result.allAccounts || [];

      // Pre-populate mappings from detected values.
      // initialBankAccountMap (from the preflight step) wins over detected for bank rows
      // so the user isn't re-prompted for a selection they already made.
      const initialBankMap = this.props.initialBankAccountMap || {};
      const mappings = {};
      ACCOUNT_ROWS.forEach(row => {
        const seeded = row.mapType === 'bank_account_map' ? initialBankMap[row.key] : null;
        if (seeded) {
          mappings[row.key] = { accountId: seeded, createInQbo: false };
          return;
        }
        const det = detected[row.key];
        if (det) {
          mappings[row.key] = { accountId: det.Id || det.id || det, createInQbo: false };
        } else {
          mappings[row.key] = { accountId: '', createInQbo: false };
        }
      });

      this.setState({ loading: false, detected, missing, allAccounts, mappings });
    } catch (error) {
      console.error('QBO setup load error:', error);
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      if (isAuthError && this.props.onAuthExpired) {
        this.props.onAuthExpired();
      }
      this.setState({
        loading: false,
        loadError: isAuthError
          ? 'Your QuickBooks connection has expired. Please close this wizard and reconnect.'
          : error.response?.data?.message || 'Failed to load QuickBooks accounts. Please try again.'
      });
    }
  };

  // ── Mapping Handlers ─────────────────────────────────────────────

  handleMappingChange = (key, accountId) => {
    this.setState(prev => ({
      mappings: {
        ...prev.mappings,
        [key]: { ...prev.mappings[key], accountId, createInQbo: false }
      }
    }));
  };

  handleCreateToggle = (key) => {
    this.setState(prev => {
      const current = prev.mappings[key] || {};
      return {
        mappings: {
          ...prev.mappings,
          [key]: { accountId: '', createInQbo: !current.createInQbo }
        }
      };
    });
  };

  // ── Validation ───────────────────────────────────────────────────

  isStep1Valid = () => {
    return ACCOUNT_ROWS.filter(r => r.required).every(row => {
      const m = this.state.mappings[row.key];
      return m && (m.accountId || m.createInQbo);
    });
  };

  // ── Navigation ───────────────────────────────────────────────────

  handleNext = () => {
    if (this.state.currentStep < 2) {
      this.setState({ currentStep: 2, saveError: null });
    }
  };

  handleBack = () => {
    if (this.state.currentStep > 1) {
      this.setState({ currentStep: 1, saveError: null });
    }
  };

  // ── Save & Activate ──────────────────────────────────────────────

  handleSaveAndActivate = async () => {
    const { mappings } = this.state;
    const { installerId } = this.props;

    this.setState({ saving: true, saveError: null });

    try {
      // Build payload — separate GL accounts from bank account. The server reads
      // camelCase `accountMap` / `bankAccountMap` only; it has no create list —
      // it creates every defined account still unmapped, so "Create" rows are
      // simply left out.
      const accountMap = {};
      const bankAccountMap = {};

      ACCOUNT_ROWS.forEach(row => {
        const m = mappings[row.key];
        if (m && m.accountId && !m.createInQbo) {
          const target = row.mapType === 'bank_account_map' ? bankAccountMap : accountMap;
          target[row.key] = m.accountId;
        }
      });

      const payload = { accountMap, bankAccountMap };

      // Step 1: Save mappings (creates missing accounts, saves config, creates service items)
      await QboAPI.saveSetupAccounts(installerId, payload);

      // Step 2: Activate sync
      this.setState({ saving: false, activating: true });
      await QboAPI.activate(installerId);

      this.setState({ activating: false });

      // Notify parent
      if (this.props.onActivated) {
        this.props.onActivated();
      }
    } catch (error) {
      console.error('QBO save/activate error:', error);
      this.setState({
        saving: false,
        activating: false,
        saveError: error.response?.data?.message || 'Failed to save account configuration. Please try again.'
      });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────

  getFilteredAccounts = (filterType) => {
    const { allAccounts } = this.state;
    const filterFn = TYPE_FILTERS[filterType];
    if (!filterFn) return allAccounts;
    return allAccounts.filter(filterFn);
  };

  isMissing = (key) => {
    return this.state.missing.includes(key);
  };

  getAccountName = (accountId) => {
    if (!accountId) return '—';
    const acct = this.state.allAccounts.find(a => String(a.id || a.Id) === String(accountId));
    return acct ? acct.name || acct.Name : `Account #${accountId}`;
  };

  // ── Render Helpers ───────────────────────────────────────────────

  renderStepIndicator = () => {
    const { currentStep } = this.state;
    const steps = [
      { num: 1, label: 'Map Accounts' },
      { num: 2, label: 'Review & Activate' }
    ];

    return (
      <div className="qbo-wizard-steps">
        <div className="d-flex justify-content-between align-items-center">
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className={`flex-fill text-center ${currentStep >= step.num ? 'text-primary' : 'text-muted'}`}>
                <div
                  className={`qbo-step-circle rounded-circle d-inline-flex align-items-center justify-content-center ${
                    currentStep > step.num ? 'bg-success text-white' :
                    currentStep === step.num ? 'bg-primary text-white' : 'bg-light'
                  }`}
                >
                  {currentStep > step.num
                    ? <i className="fa-sharp fa-regular fa-check"></i>
                    : step.num}
                </div>
                <div className="mt-2"><strong>{step.label}</strong></div>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className="flex-fill qbo-step-connector"
                  style={{ backgroundColor: currentStep > step.num ? 'var(--color-success)' : 'var(--color-bg-inset)' }}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  renderStep1 = () => {
    const { mappings, allAccounts } = this.state;
    const groups = ['gl', 'bank'];

    return (
      <div className="qbo-step-content">
        <p className="text-muted mb-4">
          Map your QuickBooks accounts to Jobvious categories. All accounts must be mapped or auto-created before activation.
        </p>

        {allAccounts.length === 0 && (
          <div className="alert alert-warning">
            <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
            <strong>No accounts loaded.</strong> Your QuickBooks chart of accounts could not be retrieved. Try closing this wizard and reopening it, or check the browser console for errors.
          </div>
        )}

        {groups.map(group => {
          const rows = ACCOUNT_ROWS.filter(r => r.group === group);
          if (rows.length === 0) return null;

          return (
            <div key={group} className="mb-4">
              <h6 className="qbo-section-header">{GROUP_LABELS[group]}</h6>
              {rows.map(row => {
                const m = mappings[row.key] || {};
                const filtered = this.getFilteredAccounts(row.filterType);
                const missing = this.isMissing(row.key);

                return (
                  <div key={row.key} className="qbo-account-row">
                    <div className="qbo-account-label">
                      <span>{row.label}</span>
                      {row.required && <span className="text-danger ml-1">*</span>}
                      {row.desc && <small className="d-block text-muted">{row.desc}</small>}
                    </div>
                    <div className="qbo-account-select">
                      {m.createInQbo ? (
                        <div className="form-control bg-light text-muted" style={{ cursor: 'default' }}>
                          <i className="fa-sharp fa-regular fa-plus-circle mr-1"></i>
                          Will be created in QBO
                        </div>
                      ) : (
                        <select
                          className="form-control"
                          value={m.accountId || ''}
                          onChange={(e) => this.handleMappingChange(row.key, e.target.value)}
                        >
                          <option value="">— Select Account —</option>
                          {filtered.map(a => (
                            <option key={a.id || a.Id} value={a.id || a.Id}>
                              {a.name || a.Name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="qbo-account-status">
                      {m.accountId && !m.createInQbo ? (
                        <i className="fa-sharp fa-solid fa-circle-check text-success" title="Mapped"></i>
                      ) : m.createInQbo ? (
                        <i className="fa-sharp fa-solid fa-circle-plus text-info" title="Will create"></i>
                      ) : missing ? (
                        <i className="fa-sharp fa-solid fa-triangle-exclamation text-warning" title="Not found in QBO"></i>
                      ) : (
                        <i className="fa-sharp fa-regular fa-circle text-muted" title="Not mapped"></i>
                      )}
                    </div>
                    <div className="qbo-account-action">
                      {(missing || (!m.accountId && !m.createInQbo)) && allAccounts.length > 0 && (
                        <button
                          className={`btn btn-sm ${m.createInQbo ? 'btn-info' : 'btn-outline-info'}`}
                          onClick={() => this.handleCreateToggle(row.key)}
                          title={m.createInQbo ? 'Cancel auto-create' : 'Create in QBO'}
                        >
                          <i className={`fa-sharp fa-regular ${m.createInQbo ? 'fa-times' : 'fa-plus'} mr-1`}></i>
                          {m.createInQbo ? 'Cancel' : 'Create'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  renderStep2 = () => {
    const { mappings } = this.state;

    const accountsToCreate = ACCOUNT_ROWS.filter(r => mappings[r.key]?.createInQbo);
    const mappedAccounts = ACCOUNT_ROWS.filter(r => mappings[r.key]?.accountId && !mappings[r.key]?.createInQbo);

    return (
      <div className="qbo-step-content">
        <p className="text-muted mb-4">
          Review your account configuration before activating QuickBooks sync.
        </p>

        {/* Mapped Accounts Summary */}
        <h6 className="qbo-section-header">Account Mappings</h6>
        <table className="table table-sm qbo-review-table">
          <thead>
            <tr>
              <th>Jobvious Account</th>
              <th>QuickBooks Account</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {mappedAccounts.map(row => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>{this.getAccountName(mappings[row.key].accountId)}</td>
                <td><span className="badge badge-success">Mapped</span></td>
              </tr>
            ))}
            {accountsToCreate.map(row => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td><em>Will be auto-created</em></td>
                <td><span className="badge badge-info">Create</span></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Auto-create info */}
        {accountsToCreate.length > 0 && (
          <div className="alert alert-info mt-3">
            <i className="fa-sharp fa-regular fa-circle-info mr-2"></i>
            <strong>{accountsToCreate.length} account{accountsToCreate.length > 1 ? 's' : ''}</strong> will be created in your QuickBooks:
            <ul className="mb-0 mt-1">
              {accountsToCreate.map(row => (
                <li key={row.key}>{row.label}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Service items info */}
        <div className="alert alert-light border mt-3">
          <i className="fa-sharp fa-regular fa-receipt mr-2"></i>
          <strong>4 service items</strong> will also be created in QuickBooks for invoicing: Labor, Materials, Permits, and Other.
        </div>
      </div>
    );
  };

  render() {
    const { show, onHide } = this.props;
    const { currentStep, loading, loadError, saving, activating, saveError } = this.state;

    const canGoNext = currentStep === 1 ? this.isStep1Valid() : true;

    return (
      <Modal show={show} onHide={onHide} size="lg" backdrop="static" className="qbo-setup-wizard-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-book mr-2"></i>
            QuickBooks Account Setup
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading QuickBooks accounts...</p>
            </div>
          ) : loadError ? (
            <div className="text-center py-5">
              <div className="alert alert-danger">
                <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
                {loadError}
              </div>
              <button className="btn btn-outline-primary" onClick={this.loadSetupAccounts}>
                <i className="fa-sharp fa-regular fa-redo mr-1"></i> Try Again
              </button>
            </div>
          ) : (
            <>
              {this.renderStepIndicator()}

              {saveError && (
                <div className="alert alert-danger mt-3">
                  <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
                  {saveError}
                </div>
              )}

              {currentStep === 1 && this.renderStep1()}
              {currentStep === 2 && this.renderStep2()}
            </>
          )}
        </Modal.Body>
        {!loading && !loadError && (
          <Modal.Footer>
            {currentStep > 1 && (
              <Button
                variant="outline-secondary"
                onClick={this.handleBack}
                disabled={saving || activating}
              >
                <i className="fa-sharp fa-regular fa-arrow-left mr-1"></i> Back
              </Button>
            )}
            <div className="ml-auto">
              <Button variant="secondary" onClick={onHide} disabled={saving || activating} className="mr-2">
                Cancel
              </Button>
              {currentStep < 2 ? (
                <Button
                  variant="primary"
                  onClick={this.handleNext}
                  disabled={!canGoNext}
                >
                  Next <i className="fa-sharp fa-regular fa-arrow-right ml-1"></i>
                </Button>
              ) : (
                <Button
                  variant="success"
                  onClick={this.handleSaveAndActivate}
                  disabled={saving || activating}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                      Saving...
                    </>
                  ) : activating ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                      Activating...
                    </>
                  ) : (
                    <>
                      <i className="fa-sharp fa-regular fa-bolt mr-1"></i>
                      Save & Activate
                    </>
                  )}
                </Button>
              )}
            </div>
          </Modal.Footer>
        )}
      </Modal>
    );
  }
}

export default QboSetupWizard;
